/**
 * useFeatureFlags
 *
 * Fetches the tenant's mobile feature flags from GET /api/mobile/features.
 * The server always returns a flags object (even when no row exists it returns
 * all-true defaults), so `data` being `undefined` means the fetch failed, not
 * that all flags are on.
 *
 * Fail-closed contract:
 *  - While loading (data still pending): flags = null, isLoading = true
 *  - On fetch error with no cached data: data stays undefined, flags = null
 *  - On fetch error with prior cached data: data = last known value (preserved)
 *  - On 401 (handled by getQueryFn on401:"returnNull"): data = null, flags = null
 *  - On success: flags = server-returned object
 *
 * FeatureFlagGuard blocks when flags is null or the specific key is false.
 * This means transient errors with no cache keep the route blocked rather than
 * silently allowing access to a disabled feature.
 */
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";

export type FeatureFlagKey =
  | "dashboard"
  | "goals"
  | "tasks"
  | "habits"
  | "journal"
  | "calendar"
  | "meditation"
  | "notepad"
  | "community"
  | "planner"
  | "gratefuls"
  | "timeBudget"
  | "aiCoach"
  | "agentTasks"
  | "aiCreate"
  | "healthMetrics"
  | "workoutPlanner"
  | "mealPlanner"
  | "media"
  | "drive"
  | "achievements"
  | "suggestions"
  | "trackerx"
  | "visionBoard";

export type FeatureFlags = Record<FeatureFlagKey, boolean>;

export function useFeatureFlags(options?: { enabled?: boolean }) {
  const { data, isLoading, isError } = useQuery<FeatureFlags | null>({
    queryKey: ["/api/mobile/features"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: options?.enabled !== false,
    staleTime: 30_000,
    // Do not retry: a single failure should fail-close immediately rather than
    // keeping the page blank for multiple retry intervals.
    retry: false,
  });

  // `data` is:
  //   - undefined  → still loading OR fetch errored (React Query doesn't reset
  //                  previously cached data on error — if there was a prior
  //                  successful response `data` will be that cached value).
  //   - null       → server returned 401 and getQueryFn returned null.
  //   - object     → successful response with the tenant's flag map.
  //
  // We never substitute an all-true fallback for undefined/null because doing
  // so would let users reach disabled features during transient API failures.
  const flags: FeatureFlags | null = data ?? null;

  return { flags, isLoading, isError };
}
